/** Colour for the caption naming what a row sits behind. */
export type RequirementCaptionColor = 'warning.main' | 'text.secondary' | 'text.disabled'

export interface RequirementState {
  /** False while a lower package is selected, which locks the row. */
  isEditable: boolean
  /** False while whatever the row sits behind — a product flag, or a value — is missing. */
  isRequirementMet: boolean
  /** Whether the product flag is switched on as things stand. */
  checked: boolean
}

export interface RequirementDisplay {
  /** The switch cannot be turned on, and says why when hovered. */
  isBlocked: boolean
  color: RequirementCaptionColor
}

/**
 * How a row presents what it sits behind: whether its switch is out of reach, and how
 * loudly the requirement is stated.
 *
 * A product flag already on without its requirement can still be switched off — that is
 * an organization stored before this rule, and switching it off is how it is resolved,
 * so only switching one on is blocked. The requirement is called out whenever it is
 * unmet either way: a product flag left on without what it needs is the one combination
 * that cannot work, and it must not read as the quietest row on the screen.
 *
 * Nothing is called out in a locked section. The row is already unavailable there, and
 * colouring it would point at work that cannot be done until a package is selected.
 *
 * Shared so a row whose requirement is a value rather than a product flag — Texting and
 * its account SID — reads the same as the rest rather than only explaining itself on
 * hover.
 */
export const requirementDisplay = ({
  isEditable,
  isRequirementMet,
  checked,
}: RequirementState): RequirementDisplay => ({
  isBlocked: !isRequirementMet && !checked,
  color: !isEditable ? 'text.disabled' : isRequirementMet ? 'text.secondary' : 'warning.main',
})
